import { clearAppStateForLocalStorage } from "@excalidraw/excalidraw/appState";
import {
  getNonDeletedElements,
  isInitializedImageElement,
} from "@excalidraw/element";
import { createStore, entries, get, set, del } from "idb-keyval";
import { nanoid } from "nanoid";

import type { ExcalidrawElement, FileId } from "@excalidraw/element/types";
import type { AppState } from "@excalidraw/excalidraw/types";

import { atom, appJotaiStore } from "../app-jotai";
import { STORAGE_KEYS } from "../app_constants";

const INDEX_KEY = "__index__";
const workplaceKey = (id: string) => `workplace:${id}`;

const store = createStore(
  `${STORAGE_KEYS.IDB_WORKPLACES}-db`,
  `${STORAGE_KEYS.IDB_WORKPLACES}-store`,
);

export type WorkplaceMeta = {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
};

export type WorkplaceData = WorkplaceMeta & {
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState>;
};

export const workplacesAtom = atom<WorkplaceMeta[]>([]);
export const activeWorkplaceIdAtom = atom<string | null>(null);

const getActiveIdFromLS = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_ACTIVE_WORKPLACE);
  } catch {
    return null;
  }
};

const setActiveIdInLS = (id: string) => {
  try {
    localStorage.setItem(STORAGE_KEYS.LOCAL_STORAGE_ACTIVE_WORKPLACE, id);
  } catch (error) {
    console.error(error);
  }
};

const loadIndex = async (): Promise<WorkplaceMeta[]> => {
  const index = await get<WorkplaceMeta[]>(INDEX_KEY, store);
  return index ?? [];
};

const saveIndex = async (index: WorkplaceMeta[]) => {
  await set(INDEX_KEY, index, store);
  appJotaiStore.set(workplacesAtom, index);
};

const createMeta = (name: string, now = Date.now()): WorkplaceMeta => ({
  id: nanoid(),
  name,
  createdAt: now,
  updatedAt: now,
});

const toPersistedAppState = (appState: AppState | Partial<AppState>) =>
  clearAppStateForLocalStorage(appState);

export class Workplaces {
  static getActiveId = (): string | null => {
    return appJotaiStore.get(activeWorkplaceIdAtom) ?? getActiveIdFromLS();
  };

  static setActiveId = (id: string) => {
    setActiveIdInLS(id);
    appJotaiStore.set(activeWorkplaceIdAtom, id);
  };

  static async refreshIndex() {
    const index = await loadIndex();
    appJotaiStore.set(workplacesAtom, index);
    return index;
  }

  /**
   * Ensures at least one workplace exists. Migrates the current localStorage
   * scene into a default workplace on first run.
   */
  static async ensureInitialized(opts: {
    elements: readonly ExcalidrawElement[];
    appState: AppState | Partial<AppState> | null;
  }): Promise<{ activeId: string; index: WorkplaceMeta[] }> {
    let index = await loadIndex();
    let activeId = getActiveIdFromLS();

    if (index.length === 0) {
      const name =
        (opts.appState && "name" in opts.appState && opts.appState.name) ||
        "Untitled";
      const meta = createMeta(String(name || "Untitled"));
      const data: WorkplaceData = {
        ...meta,
        elements: getNonDeletedElements(opts.elements),
        appState: opts.appState ? toPersistedAppState(opts.appState) : {},
      };
      await set(workplaceKey(meta.id), data, store);
      index = [meta];
      await saveIndex(index);
      activeId = meta.id;
      this.setActiveId(activeId);
    } else {
      appJotaiStore.set(workplacesAtom, index);
      if (!activeId || !index.some((w) => w.id === activeId)) {
        activeId = index[0].id;
        this.setActiveId(activeId);
      } else {
        appJotaiStore.set(activeWorkplaceIdAtom, activeId);
      }
    }

    return { activeId: activeId!, index };
  }

  static async load(id: string): Promise<WorkplaceData | null> {
    return (await get<WorkplaceData>(workplaceKey(id), store)) ?? null;
  }

  static async saveActive(
    elements: readonly ExcalidrawElement[],
    appState: AppState,
  ) {
    const activeId = this.getActiveId();
    if (!activeId) {
      return;
    }

    const existing = await this.load(activeId);
    const now = Date.now();
    const name =
      existing?.name ||
      (appState.name ? String(appState.name) : "Untitled") ||
      "Untitled";

    const meta: WorkplaceMeta = {
      id: activeId,
      name,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };

    const data: WorkplaceData = {
      ...meta,
      elements: getNonDeletedElements(elements),
      appState: {
        ...toPersistedAppState(appState),
        name,
      },
    };

    await set(workplaceKey(activeId), data, store);

    const index = await loadIndex();
    const nextIndex = index.map((item) => (item.id === activeId ? meta : item));
    // if somehow missing from index, append
    if (!nextIndex.some((item) => item.id === activeId)) {
      nextIndex.unshift(meta);
    }
    await saveIndex(nextIndex);
  }

  static async create(opts?: {
    name?: string;
    elements?: readonly ExcalidrawElement[];
    appState?: AppState | Partial<AppState> | null;
  }): Promise<WorkplaceData> {
    const now = Date.now();
    const meta = createMeta(opts?.name?.trim() || "Untitled", now);
    const data: WorkplaceData = {
      ...meta,
      elements: getNonDeletedElements(opts?.elements ?? []),
      appState: {
        ...(opts?.appState ? toPersistedAppState(opts.appState) : {}),
        name: meta.name,
      },
    };

    await set(workplaceKey(meta.id), data, store);
    const index = await loadIndex();
    await saveIndex([meta, ...index]);
    return data;
  }

  static async rename(id: string, name: string) {
    const trimmed = name.trim() || "Untitled";
    const existing = await this.load(id);
    if (!existing) {
      return;
    }

    const updated: WorkplaceData = {
      ...existing,
      name: trimmed,
      updatedAt: Date.now(),
      appState: {
        ...existing.appState,
        name: trimmed,
      },
    };
    await set(workplaceKey(id), updated, store);

    const index = await loadIndex();
    await saveIndex(
      index.map((item) =>
        item.id === id
          ? { ...item, name: trimmed, updatedAt: updated.updatedAt }
          : item,
      ),
    );
  }

  static async remove(id: string): Promise<boolean> {
    const index = await loadIndex();
    if (index.length <= 1) {
      return false;
    }

    await del(workplaceKey(id), store);
    const nextIndex = index.filter((item) => item.id !== id);
    await saveIndex(nextIndex);

    if (this.getActiveId() === id) {
      this.setActiveId(nextIndex[0].id);
    }
    return true;
  }

  /** File IDs referenced by any workplace (for image GC). */
  static async getAllFileIds(): Promise<FileId[]> {
    const ids = new Set<FileId>();
    const all = await entries(store);
    for (const [key, value] of all) {
      if (typeof key !== "string" || !key.startsWith("workplace:")) {
        continue;
      }
      const data = value as WorkplaceData;
      for (const element of data.elements || []) {
        if (isInitializedImageElement(element)) {
          ids.add(element.fileId);
        }
      }
    }
    return [...ids];
  }
}
